/**
 * Material Takeoff Tool
 * 
 * Extracts material quantities with cost estimation.
 */

import { v4 as uuidv4 } from 'uuid';

// ==================== Types ====================

export interface MaterialDefinition {
    id: string;
    name: string;
    category: string;
    unitCost: number;
    unit: 'each' | 'm' | 'm²' | 'm³' | 'kg' | 'ton';
    density?: number;  // kg/m³
    wasteFactor: number;  // 1.05 = 5% waste
}

export interface MaterialQuantity {
    materialId: string;
    materialName: string;
    category: string;
    rawQuantity: number;
    adjustedQuantity: number;
    unit: string;
    unitCost: number;
    totalCost: number;
    sourceElements: string[];
}

export interface TakeoffResult {
    id: string;
    name: string;
    generatedAt: number;
    materials: MaterialQuantity[];
    subtotalByCategory: Record<string, number>;
    grandTotal: number;
    elementCount: number;
}

// ==================== Standard Materials Database ====================

// Shared catalog, also consumed by the Manage > Materials dialog.
export const STANDARD_MATERIALS: MaterialDefinition[] = [    // Concrete
    { id: 'concrete-c25', name: 'Concrete C25/30', category: 'Concrete', unitCost: 120, unit: 'm³', density: 2400, wasteFactor: 1.05 },
    { id: 'concrete-c30', name: 'Concrete C30/37', category: 'Concrete', unitCost: 140, unit: 'm³', density: 2400, wasteFactor: 1.05 },
    { id: 'rebar-b500', name: 'Rebar B500B', category: 'Steel', unitCost: 1.2, unit: 'kg', density: 7850, wasteFactor: 1.10 },

    // Steel
    { id: 'steel-structural', name: 'Structural Steel', category: 'Steel', unitCost: 2.5, unit: 'kg', density: 7850, wasteFactor: 1.05 },
    { id: 'steel-galvanized', name: 'Galvanized Steel Sheet', category: 'Steel', unitCost: 35, unit: 'm²', wasteFactor: 1.10 },

    // Masonry
    { id: 'brick-standard', name: 'Standard Brick', category: 'Masonry', unitCost: 0.50, unit: 'each', wasteFactor: 1.05 },
    { id: 'block-concrete', name: 'Concrete Block 200mm', category: 'Masonry', unitCost: 2.50, unit: 'each', wasteFactor: 1.03 },

    // Finishes
    { id: 'gypsum-board', name: 'Gypsum Board 12.5mm', category: 'Finishes', unitCost: 8, unit: 'm²', wasteFactor: 1.08 },
    { id: 'paint-interior', name: 'Interior Paint', category: 'Finishes', unitCost: 15, unit: 'm²', wasteFactor: 1.10 },

    // MEP
    { id: 'duct-galv', name: 'Galvanized Duct', category: 'HVAC', unitCost: 45, unit: 'm', wasteFactor: 1.05 },
    { id: 'pipe-copper', name: 'Copper Pipe', category: 'Plumbing', unitCost: 25, unit: 'm', wasteFactor: 1.08 },
    { id: 'pipe-pvc', name: 'PVC Pipe', category: 'Plumbing', unitCost: 8, unit: 'm', wasteFactor: 1.05 },
    { id: 'conduit-emt', name: 'EMT Conduit', category: 'Electrical', unitCost: 12, unit: 'm', wasteFactor: 1.05 },
    { id: 'cable-tray', name: 'Cable Tray', category: 'Electrical', unitCost: 55, unit: 'm', wasteFactor: 1.03 },
];

// ==================== Category to Material Mapping ====================

const CATEGORY_MATERIALS: Record<string, string[]> = {
    'Walls': ['concrete-c25', 'brick-standard', 'gypsum-board'],
    'Floors': ['concrete-c30', 'rebar-b500'],
    'Roofs': ['concrete-c25', 'steel-structural'],
    'Structural Columns': ['concrete-c30', 'rebar-b500', 'steel-structural'],
    'Structural Framing': ['steel-structural', 'concrete-c30'],
    'Ducts': ['duct-galv'],
    'Pipes': ['pipe-copper', 'pipe-pvc'],
    'Conduits': ['conduit-emt'],
    'Cable Trays': ['cable-tray'],
};

// ==================== Material Record Adapters ====================

/**
 * Registry bridge: convert the document material registry (BIM-like
 * BIMMaterial records) into takeoff-compatible MaterialDefinition entries.
 * When a project registry is supplied, it wins over the static catalog.
 */
export interface RegistryMaterialLike {
    id: string;
    name: string;
    identity: {
        class: string;
        cost: string;
        costBasis: string;
        takeoffUnit: 'each' | 'm' | 'm²' | 'm³' | 'kg' | 'ton';
        wasteFactor: number;
    };
    physical: { density: number };
}

export function registryToDefinition(mat: RegistryMaterialLike): MaterialDefinition {
    const unitCost = parseFloat(mat.identity.cost);
    return {
        id: mat.id,
        name: mat.name,
        category: mat.identity.class,
        unitCost: Number.isFinite(unitCost) ? unitCost : 0,
        unit: mat.identity.takeoffUnit,
        density: mat.physical.density,
        wasteFactor: mat.identity.wasteFactor || 1,
    };
}

// ==================== Material Takeoff Tool ====================

export class MaterialTakeoffTool {
    private materials: Map<string, MaterialDefinition> = new Map();
    private customCosts: Map<string, number> = new Map();

    constructor() {
        for (const mat of STANDARD_MATERIALS) {
            this.materials.set(mat.id, mat);
        }
    }

    /** Refresh the internal map from a document registry (Manage > Materials). */
    public syncFromRegistry(registry: Iterable<RegistryMaterialLike>): void {
        for (const mat of registry) {
            this.materials.set(mat.id, registryToDefinition(mat));
        }
    }

    // ==================== Takeoff Generation ====================

    public generateTakeoff(
        elements: any[],
        name: string = 'Material Takeoff',
        registry?: Iterable<RegistryMaterialLike>,
    ): TakeoffResult {
        if (registry) this.syncFromRegistry(registry);
        const quantities = new Map<string, MaterialQuantity>();

        for (const element of elements) {
            const materialIds = CATEGORY_MATERIALS[element.category] || [];

            for (const matId of materialIds) {
                const material = this.materials.get(matId);
                if (!material) continue;

                const quantity = this.extractQuantity(element, material);
                if (quantity <= 0) continue;

                const existing = quantities.get(matId);
                if (existing) {
                    existing.rawQuantity += quantity;
                    existing.adjustedQuantity += quantity * material.wasteFactor;
                    existing.totalCost = existing.adjustedQuantity * existing.unitCost;
                    existing.sourceElements.push(element.id);
                } else {
                    const adjustedQty = quantity * material.wasteFactor;
                    const unitCost = this.customCosts.get(matId) || material.unitCost;
                    quantities.set(matId, {
                        materialId: matId,
                        materialName: material.name,
                        category: material.category,
                        rawQuantity: quantity,
                        adjustedQuantity: adjustedQty,
                        unit: material.unit,
                        unitCost,
                        totalCost: adjustedQty * unitCost,
                        sourceElements: [element.id]
                    });
                }
            }
        }

        // Calculate subtotals by category
        const subtotalByCategory: Record<string, number> = {};
        let grandTotal = 0;

        for (const qty of quantities.values()) {
            subtotalByCategory[qty.category] = (subtotalByCategory[qty.category] || 0) + qty.totalCost;
            grandTotal += qty.totalCost;
        }

        return {
            id: uuidv4(),
            name,
            generatedAt: Date.now(),
            materials: Array.from(quantities.values()),
            subtotalByCategory,
            grandTotal,
            elementCount: elements.length
        };
    }

    private extractQuantity(element: any, material: MaterialDefinition): number {
        const params = element.parameters || {};
        const gd = element.geometryData;

        switch (material.unit) {
            case 'm³':
                // Volume
                if (params['Volume']) return this.parseValue(params['Volume']);
                if (gd?.boundary && gd?.thickness) {
                    return this.calculatePolygonArea(gd.boundary) * gd.thickness;
                }
                return 0;

            case 'm²':
                // Area
                if (params['Area']) return this.parseValue(params['Area']);
                if (gd?.boundary) {
                    return this.calculatePolygonArea(gd.boundary);
                }
                if (gd?.start && gd?.end && gd?.height) {
                    return this.calculateDistance(gd.start, gd.end) * gd.height;
                }
                return 0;

            case 'm':
                // Length
                if (params['Length']) return this.parseValue(params['Length']);
                if (gd?.start && gd?.end) {
                    return this.calculateDistance(gd.start, gd.end);
                }
                return 0;

            case 'kg':
                // Weight
                if (params['Weight']) return this.parseValue(params['Weight']);
                if (material.density) {
                    const volume = this.extractQuantity(element, { ...material, unit: 'm³' });
                    return volume * material.density;
                }
                return 0;

            case 'each':
                return 1;

            default:
                return 0;
        }
    }

    private parseValue(value: any): number {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const match = value.match(/[\d.]+/);
            return match ? parseFloat(match[0]) : 0;
        }
        return 0;
    }

    private calculatePolygonArea(boundary: [number, number][]): number {
        if (boundary.length < 3) return 0;
        let area = 0;
        for (let i = 0; i < boundary.length; i++) {
            const j = (i + 1) % boundary.length;
            area += boundary[i][0] * boundary[j][1];
            area -= boundary[j][0] * boundary[i][1];
        }
        return Math.abs(area) / 2;
    }

    private calculateDistance(start: number[], end: number[]): number {
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const dz = (end[2] || 0) - (start[2] || 0);
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    // ==================== Cost Management ====================

    public setUnitCost(materialId: string, cost: number): void {
        this.customCosts.set(materialId, cost);
    }

    public getMaterial(id: string): MaterialDefinition | undefined {
        return this.materials.get(id);
    }

    public getAllMaterials(): MaterialDefinition[] {
        return Array.from(this.materials.values());
    }

    public addCustomMaterial(material: MaterialDefinition): void {
        this.materials.set(material.id, material);
    }

    // ==================== Export ====================

    public exportToCSV(result: TakeoffResult): string {
        const lines: string[] = [];
        lines.push('Material,Category,Quantity,Unit,Unit Cost,Total Cost');

        for (const mat of result.materials) {
            lines.push([
                mat.materialName,
                mat.category,
                mat.adjustedQuantity.toFixed(2),
                mat.unit,
                mat.unitCost.toFixed(2),
                mat.totalCost.toFixed(2)
            ].join(','));
        }

        lines.push('');
        lines.push('Category Subtotals,,,,,');
        for (const [cat, total] of Object.entries(result.subtotalByCategory)) {
            lines.push(`${cat},,,,,${total.toFixed(2)}`);
        }

        lines.push('');
        lines.push(`GRAND TOTAL,,,,,${result.grandTotal.toFixed(2)}`);

        return lines.join('\n');
    }
}
